import { NotAllowedIcon, TimeIcon } from "@chakra-ui/icons";
import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import { BsArchive, BsCheckCircle } from "react-icons/bs";
import { LuFiles } from "react-icons/lu";
import { MdChecklist, MdIncompleteCircle } from "react-icons/md";
import { useIntl } from "react-intl";

import { ScenarioStatus as ScenarioStatusType } from "@nens/ldo/api-v1";

export function useScenarioStatusDescription() {
  const intl = useIntl();

  return function (status?: string): string {
    const statusText =
      status === "incomplete"
        ? intl.formatMessage({ id: "status.incomplete", defaultMessage: "Incomplete" })
        : status === "files_present"
        ? intl.formatMessage({ id: "status.files_present", defaultMessage: "Files present" })
        : status === "processing"
        ? intl.formatMessage({ id: "status.processing", defaultMessage: "Processing" })
        : status === "complete"
        ? intl.formatMessage({ id: "status.complete", defaultMessage: "Complete" })
        : status === "import_checked"
        ? intl.formatMessage({ id: "status.import_checked", defaultMessage: "Import Checked" })
        : status === "quality_control_requested"
        ? intl.formatMessage({
            id: "status.quality_control_requested",
            defaultMessage: "QC requested",
          })
        : status === "quality_checked"
        ? intl.formatMessage({ id: "status.quality_checked", defaultMessage: "Quality checked" })
        : status === "quality_rejected"
        ? intl.formatMessage({ id: "status.quality_rejected", defaultMessage: "QC Rejected" })
        : status === "archived"
        ? intl.formatMessage({ id: "status.archived", defaultMessage: "Archived" })
        : intl.formatMessage({ id: "status.unknown", defaultMessage: "Unknown" });

    return statusText;
  };
}
export function ScenarioStatus({ status }: { status: ScenarioStatusType | undefined }) {
  const ARCHIVED_COLOR = "blue";
  const COMPLETE_COLOR = "green";
  const FILES_PRESENT_COLOR = "yellow";
  const IMPORT_CHECKED_COLOR = "purple";
  const INCOMPLETE_COLOR = "orange";
  const QC_REQUESTED_COLOR = "pink";
  const QUALITY_CHECKED_COLOR = "teal";
  const QUALITY_REJECTED_COLOR = "red";
  const UNDEFINED_COLOR = "gray";

  const getStatusText = useScenarioStatusDescription();

  const statusText = getStatusText(status);

  const statusColor =
    status === "incomplete"
      ? INCOMPLETE_COLOR
      : status === "files_present" || status === "processing"
      ? FILES_PRESENT_COLOR
      : status === "complete"
      ? COMPLETE_COLOR
      : status === "import_checked"
      ? IMPORT_CHECKED_COLOR
      : status === "quality_control_requested"
      ? QC_REQUESTED_COLOR
      : status === "quality_checked"
      ? QUALITY_CHECKED_COLOR
      : status === "quality_rejected"
      ? QUALITY_REJECTED_COLOR
      : status === "archived"
      ? ARCHIVED_COLOR
      : UNDEFINED_COLOR;

  const icon =
    status === "incomplete" ? (
      <Box mr={1}>
        <MdIncompleteCircle />
      </Box>
    ) : status === "files_present" || status === "processing" ? (
      <Box mr={1}>
        <LuFiles />
      </Box>
    ) : status === "complete" ? (
      <Box mr={1}>
        <MdChecklist />
      </Box>
    ) : status === "import_checked" ? (
      <Box mr={1}>
        <BsCheckCircle />
      </Box>
    ) : status === "quality_control_requested" ? (
      <TimeIcon mr={1} />
    ) : status === "quality_checked" ? (
      <Box mr={1}>
        <BsCheckCircle />
      </Box>
    ) : status === "quality_rejected" ? (
      <NotAllowedIcon mr={1} />
    ) : status === "archived" ? (
      <Box mr="1">
        <BsArchive />
      </Box>
    ) : null;

  return (
    <Badge
      rounded="full"
      px="12px"
      py="4px"
      colorScheme={statusColor}
      fontSize="0.8em"
      fontWeight="semibold"
      cursor="pointer"
    >
      <Flex alignItems="center">
        {icon}
        <Text ml="4px">{statusText}</Text>
      </Flex>
    </Badge>
  );
}
